let policyDraftingConfig = null;

/** Mirrors the backend's `PolicyPreviewDraftStatus`. */
export const DRAFT_STATUS = {
  WORKING: 'WORKING',
  IN_REVIEW: 'IN_REVIEW',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  APPROVED: 'APPROVED',
  DENIED: 'DENIED',
  DELETED: 'DELETED'
};

/**
 * The backend owns the drafting gate (`getUserPreferences.policyDrafting`); auth stores it here once
 * preferences load. Until then every helper answers false, so no drafting UI shows before the gate is known.
 */
export const setPolicyDraftingConfig = (policyDrafting) => {
  policyDraftingConfig = policyDrafting ?? null;
};

export const isPolicyDraftingSupported = () => Boolean(policyDraftingConfig?.enabled);

export const isDraftablePolicyType = (policyType) =>
  Boolean(isPolicyDraftingSupported() && policyType && policyDraftingConfig.policyTypes.includes(policyType));

/**
 * A preview generated on a draftable policy carries `unsavedDraft`, the record it would become if saved, until it
 * is saved and `draft` takes over; previews of other policies carry neither.
 */
const getDraftRecord = (preview) => preview?.draft ?? preview?.unsavedDraft;

export const getDraftStatus = (preview) => {
  const draft = getDraftRecord(preview);
  return draft?.status ?? DRAFT_STATUS.WORKING;
};

export const getDraftStatusChange = (preview) => {
  const draft = getDraftRecord(preview);
  if (draft?.status === DRAFT_STATUS.DELETED) {
    return { by: draft.deletedBy, at: draft.deletedAt };
  }
  return { by: draft?.createdBy, at: draft?.createdAt };
};

/** At most one draft on a policy holds one of these statuses at a time. */
const BLOCKING_DRAFT_STATUSES = [DRAFT_STATUS.IN_REVIEW, DRAFT_STATUS.CHANGES_REQUESTED, DRAFT_STATUS.APPROVED];

/**
 * The blocking draft, other than the preview's own: an unsaved edit of a blocking draft shares its draftId while the
 * blocking record is still the one listed, and a draft never blocks itself.
 */
export const getBlockingDraft = (previewDrafts, preview) => {
  const ownDraftId = getDraftRecord(preview)?.draftId;
  return previewDrafts?.find(
    (previewDraft) =>
      BLOCKING_DRAFT_STATUSES.includes(previewDraft.status) &&
      (ownDraftId === undefined || previewDraft.draftId !== ownDraftId)
  );
};

/** `previewDrafts` is null when the list failed to load; the policy then stays locked as if a draft were blocking. */
export const hasBlockingDraft = (previewDrafts, preview) =>
  previewDrafts === null || Boolean(getBlockingDraft(previewDrafts, preview));

/** While a draft is blocking, the official policy and every working draft can only be viewed. */
export const isViewOnlyWhileBlocked = ({ preview, previewDrafts }) =>
  hasBlockingDraft(previewDrafts, preview) && getDraftStatus(preview) === DRAFT_STATUS.WORKING;

/**
 * The latest version of a saved draft when the preview is an older one: every step writes a new record under the
 * draft's draftId, and the list holds only the latest. A listed record older than the preview's is a list that hasn't
 * caught up with a save yet, not a newer version.
 */
export const getNewerDraftVersion = ({ preview, previewDrafts }) => {
  const draft = preview?.draft;
  if (!draft || !previewDrafts || previewDrafts.some((previewDraft) => previewDraft.previewId === preview.id)) {
    return undefined;
  }
  return previewDrafts.find(
    (previewDraft) => previewDraft.draftId === draft.draftId && previewDraft.lastUpdatedAt > draft.createdAt
  );
};

const READ_ONLY_DRAFT_STATUSES = [DRAFT_STATUS.DENIED, DRAFT_STATUS.DELETED];

/**
 * Whether the viewed policy or draft can only be viewed: a drafts list that failed to load, the block, a draft in
 * review for anyone but an underwriter, a denied or deleted draft, and an older version of a draft.
 */
export const isLockedByDrafting = ({ previewDrafts, preview, canApproveUWReview }) =>
  previewDrafts === null ||
  isViewOnlyWhileBlocked({ preview, previewDrafts }) ||
  (getDraftStatus(preview) === DRAFT_STATUS.IN_REVIEW && !canApproveUWReview) ||
  (Boolean(preview?.draft) && READ_ONLY_DRAFT_STATUSES.includes(getDraftStatus(preview))) ||
  Boolean(getNewerDraftVersion({ preview, previewDrafts }));

export const DRAFT_UNAVAILABLE_REASON = {
  BLOCKED_BY_OTHER_DRAFT: 'blockedByOtherDraft',
  OUTDATED: 'outdated',
  DELETED: 'deleted',
  NEWER_VERSION: 'newerVersion'
};

/** Why a draft opens behind a blocking modal, or null when it opens normally. An expired preview never loads at all. */
export const getDraftUnavailableReason = ({ preview, previewDrafts, isPreviewExpired }) => {
  if (isPreviewExpired) {
    return DRAFT_UNAVAILABLE_REASON.OUTDATED;
  }
  if (!preview?.draft) {
    return null;
  }
  if (getDraftStatus(preview) === DRAFT_STATUS.DELETED) {
    return DRAFT_UNAVAILABLE_REASON.DELETED;
  }
  if (getNewerDraftVersion({ preview, previewDrafts })) {
    return DRAFT_UNAVAILABLE_REASON.NEWER_VERSION;
  }
  if (getBlockingDraft(previewDrafts, preview) && getDraftStatus(preview) === DRAFT_STATUS.WORKING) {
    return DRAFT_UNAVAILABLE_REASON.BLOCKED_BY_OTHER_DRAFT;
  }
  return null;
};
